import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { GraduationCap, Menu, X } from 'lucide-react';
import { Button, cn } from '@tutor/ui';
import { useAuthStore } from '../../stores/auth.store';

const NAV_LINKS = [
  { href: '#how-it-works', label: 'How it works' },
  { href: '#subjects', label: 'Subjects' },
  { href: '#classroom', label: 'The classroom' },
  { href: '#for-tutors', label: 'For tutors' },
  { href: '#faq', label: 'FAQ' },
];

export function PublicLayout({ children }: { children: ReactNode }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const status = useAuthStore((state) => state.status);
  const homeRoute = useAuthStore((state) => state.homeRoute);
  const isSignedIn = status === 'authenticated';

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <header className="sticky top-0 z-40 border-b border-slate-200/80 bg-white/85 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-6xl items-center gap-6 px-4 sm:px-6 lg:px-8">
          <Link to="/" className="flex shrink-0 items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-600 text-white">
              <GraduationCap className="h-4.5 w-4.5" aria-hidden />
            </span>
            <span className="text-sm font-semibold tracking-tight text-slate-900">Tutor Platform</span>
          </Link>

          <nav className="hidden flex-1 items-center gap-1 md:flex">
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="rounded-md px-3 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900"
              >
                {link.label}
              </a>
            ))}
          </nav>

          <div className="ml-auto hidden items-center gap-2 md:flex">
            {isSignedIn ? (
              <Link to={homeRoute()}>
                <Button size="sm">Go to dashboard</Button>
              </Link>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" size="sm">
                    Sign in
                  </Button>
                </Link>
                <Link to="/register">
                  <Button size="sm">Get started</Button>
                </Link>
              </>
            )}
          </div>

          <Button
            variant="ghost"
            size="sm"
            className="ml-auto md:hidden"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? <X className="h-5 w-5" aria-hidden /> : <Menu className="h-5 w-5" aria-hidden />}
          </Button>
        </div>

        {menuOpen && (
          <div className="border-t border-slate-200 bg-white px-4 py-3 md:hidden">
            <nav className="flex flex-col gap-1">
              {NAV_LINKS.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setMenuOpen(false)}
                  className="rounded-md px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100"
                >
                  {link.label}
                </a>
              ))}
            </nav>
            <div className="mt-3 flex flex-col gap-2 border-t border-slate-100 pt-3">
              {isSignedIn ? (
                <Link to={homeRoute()} onClick={() => setMenuOpen(false)}>
                  <Button fullWidth>Go to dashboard</Button>
                </Link>
              ) : (
                <>
                  <Link to="/login" onClick={() => setMenuOpen(false)}>
                    <Button variant="outline" fullWidth>
                      Sign in
                    </Button>
                  </Link>
                  <Link to="/register" onClick={() => setMenuOpen(false)}>
                    <Button fullWidth>Get started</Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <div className="flex items-center gap-2.5">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-600 text-white">
                  <GraduationCap className="h-4.5 w-4.5" aria-hidden />
                </span>
                <span className="text-sm font-semibold tracking-tight text-slate-900">Tutor Platform</span>
              </div>
              <p className="mt-3 max-w-xs text-sm text-slate-500">
                One-to-one online tutoring with a live classroom, homework that gets marked,
                and progress both sides can see.
              </p>
            </div>

            <FooterColumn
              title="Learning"
              links={[
                { href: '#how-it-works', label: 'How it works' },
                { href: '#subjects', label: 'Subjects and levels' },
                { href: '#classroom', label: 'The online classroom' },
                { href: '#parents', label: 'For parents' },
              ]}
            />

            <FooterColumn
              title="Tutoring"
              links={[
                { href: '#for-tutors', label: 'Become a tutor' },
                { href: '#faq', label: 'Questions' },
              ]}
            />

            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Account</h3>
              <ul className="mt-3 space-y-2">
                <li>
                  <Link to="/login" className="text-sm text-slate-600 hover:text-slate-900">
                    Sign in
                  </Link>
                </li>
                <li>
                  <Link to="/register" className="text-sm text-slate-600 hover:text-slate-900">
                    Create an account
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-10 flex flex-col gap-2 border-t border-slate-200 pt-6 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-slate-500">
              © {new Date().getFullYear()} Tutor Platform. All rights reserved.
            </p>
            <p className="text-xs text-slate-400">
              A demonstration build — not a live tutoring service.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

function FooterColumn({
  title,
  links,
}: {
  title: string;
  links: Array<{ href: string; label: string }>;
}) {
  return (
    <div>
      <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</h3>
      <ul className={cn('mt-3 space-y-2')}>
        {links.map((link) => (
          <li key={link.href}>
            <a href={link.href} className="text-sm text-slate-600 hover:text-slate-900">
              {link.label}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}
